import { useRef, useState } from 'react'
import { Button, Modal, Spinner } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'
import { AlertTriangleIcon, CheckCircleIcon, DownloadIcon, UploadIcon } from '@/components/ui/icons'

import { useProductImport } from '../hooks'
import type { ProductImportResult } from '../types'

interface ImportProductsDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

/** Matches the server's own ceiling, so an oversized file is refused before it travels. */
const MAX_FILE_BYTES = 10 * 1024 * 1024

/** Past this the list stops being a list and starts being a wall. */
const ERRORS_SHOWN = 50

/**
 * Bulk import, in three steps on one screen.
 *
 * Pick a file, see what it would do, then let it. The middle step is the point:
 * a four-hundred-row sheet is exactly the thing a merchant will not upload on
 * faith, and the dry run costs one request and nothing else. It also means the
 * error list — which is the whole value when a file is wrong — arrives before
 * anything has been written rather than after half of it has.
 *
 * Not built on `FormModal`: that frame has one submit button with one label and
 * a single busy state, and this has a button whose label and meaning change
 * under it.
 */
export function ImportProductsDialog({ isOpen, onOpenChange }: ImportProductsDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { downloadTemplate, submit } = useProductImport()

  /* The last answer from the server, for the file currently picked. */
  const result = submit.data ?? null
  const isDone = Boolean(result?.applied)
  const isReady = Boolean(result && !result.errors.length && !result.applied)

  function reset() {
    setFile(null)
    setLocalError(null)
    submit.reset()

    // Clearing lets the same file be picked again — the input fires no change
    // event when the selection has not changed.
    if (inputRef.current) inputRef.current.value = ''
  }

  // The dialog is not remounted per open, so the previous run's file and report
  // have to be cleared on the way out rather than on the way in.
  function close() {
    reset()
    onOpenChange(false)
  }

  function pick(picked: File | undefined) {
    submit.reset()
    setLocalError(null)

    if (!picked) return

    if (!/\.xlsx$/i.test(picked.name)) {
      setLocalError('Pick the .xlsx template. Other spreadsheet formats cannot be read.')
      return
    }

    if (picked.size > MAX_FILE_BYTES) {
      setLocalError('That file is over 10 MB. Split it and import the halves.')
      return
    }

    setFile(picked)
  }

  function advance() {
    if (isDone) return close()
    if (!file) return

    submit.mutate({ file, mode: isReady ? 'import' : 'validate' })
  }

  const actionLabel = isDone ? 'Done' : isReady ? `Import ${countLabel(result!)}` : 'Check the file'

  const busyLabel = isReady ? 'Importing…' : 'Checking…'

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && close()}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="lg" placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            <Modal.Header className="gap-1 pe-10">
              <Modal.Heading>Import products</Modal.Heading>
              <p className="text-muted text-xs leading-relaxed">
                Fill in the template and upload it. Nothing is saved until the whole file passes.
              </p>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-4">
              <div className="border-border flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{file ? file.name : 'No file chosen'}</p>
                  <p className="text-muted text-xs">
                    {file ? fileSize(file.size) : 'An .xlsx file, up to 10 MB.'}
                  </p>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="sr-only"
                  onChange={(event) => pick(event.target.files?.[0])}
                />

                <Button
                  variant="ghost"
                  size="sm"
                  className="border-border h-9 shrink-0 rounded-full border px-4 text-xs"
                  isDisabled={submit.isPending}
                  onPress={() => inputRef.current?.click()}
                >
                  <UploadIcon width={14} height={14} />
                  {file ? 'Choose another' : 'Choose file'}
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="text-muted h-8 justify-start rounded-full px-2 text-xs"
                isDisabled={downloadTemplate.isPending}
                onPress={() => downloadTemplate.mutate()}
              >
                {downloadTemplate.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <DownloadIcon width={14} height={14} />
                )}
                Download the blank template
              </Button>

              {downloadTemplate.error != null && (
                <p role="alert" className="text-danger text-xs">
                  {errorMessage(downloadTemplate.error, 'Could not download the template.')}
                </p>
              )}

              {result && <ImportReport result={result} />}

              {localError && (
                <p role="alert" className="text-danger text-xs">
                  {localError}
                </p>
              )}

              {submit.error != null && (
                <p role="alert" className="text-danger text-xs">
                  {errorMessage(submit.error, 'Could not read that file. Please try again.')}
                </p>
              )}
            </Modal.Body>

            <Modal.Footer>
              {!isDone && (
                <Button variant="ghost" onPress={close} isDisabled={submit.isPending}>
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                onPress={advance}
                isDisabled={(!file && !isDone) || submit.isPending}
              >
                {submit.isPending && <Spinner size="sm" />}
                {submit.isPending ? busyLabel : actionLabel}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}

/** What the run came to — the same panel whether it was a dry run or the real thing. */
function ImportReport({ result }: { result: ProductImportResult }) {
  const hasErrors = result.errors.length > 0

  if (result.applied) {
    return (
      <div className="border-border flex flex-col gap-2 rounded-xl border p-4">
        <p className="flex items-center gap-2 text-sm font-medium">
          <CheckCircleIcon width={16} height={16} className="text-success" />
          {summaryOf(result)}
        </p>

        {/* A save that failed on its own terms after the file passed. Rare, and
            the rest of the file did go in — so it is reported, not thrown. */}
        {hasErrors && <ErrorList errors={result.errors} lead="These did not save:" />}
      </div>
    )
  }

  if (hasErrors) {
    return (
      <div className="border-danger/40 bg-danger/5 flex flex-col gap-2 rounded-xl border p-4">
        <p className="flex items-center gap-2 text-sm font-medium">
          <AlertTriangleIcon width={16} height={16} className="text-danger" />
          {result.errors.length === 1
            ? 'One row needs fixing. Nothing was saved.'
            : `${result.errors.length} rows need fixing. Nothing was saved.`}
        </p>

        <ErrorList errors={result.errors} lead="Fix these in the sheet and upload it again:" />
      </div>
    )
  }

  return (
    <div className="border-border flex flex-col gap-1 rounded-xl border p-4">
      <p className="flex items-center gap-2 text-sm font-medium">
        <CheckCircleIcon width={16} height={16} className="text-success" />
        The file checks out.
      </p>
      <p className="text-muted text-xs">{summaryOf(result)}</p>
    </div>
  )
}

function ErrorList({ errors, lead }: { errors: ProductImportResult['errors']; lead: string }) {
  const shown = errors.slice(0, ERRORS_SHOWN)

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted text-xs">{lead}</p>

      <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto pe-1 text-xs">
        {shown.map((error, index) => (
          <li key={`${error.row}-${index}`} className="flex gap-2">
            <span className="text-muted shrink-0 tabular-nums">Row {error.row}</span>
            <span>{error.message}</span>
          </li>
        ))}
      </ul>

      {errors.length > shown.length && (
        <p className="text-muted text-xs">
          …and {errors.length - shown.length} more. Fix these first — some of the rest are often the
          same mistake repeated.
        </p>
      )}
    </div>
  )
}

/** `18 products` / `1 product` — the noun the button and the summary share. */
function countLabel({ products }: ProductImportResult): string {
  return products === 1 ? '1 product' : `${products} products`
}

function summaryOf(result: ProductImportResult): string {
  const { rows, created, updated, applied } = result
  const rowLabel = rows === 1 ? '1 row' : `${rows} rows`
  const parts = [
    `${created} ${applied ? 'added' : 'to add'}`,
    `${updated} ${applied ? 'updated' : 'to update'}`,
  ]

  return `${rowLabel}, ${countLabel(result)} — ${parts.join(', ')}.`
}

/** Rounded the way a file manager rounds it, which is what the merchant will compare against. */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
